import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, ChevronLeft, ChevronRight, MessageSquare, MoreVertical, Archive, ArchiveRestore, Trash2, Pencil, Eye, XCircle, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { useAuth } from "@/contexts/AuthContext";
import { useChatSystem } from "@/hooks/useChatSystem";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";
import { formatEquipmentType } from "@/lib/equipmentTypes";
import { DispatchStatusBadge } from "@/components/lr-direct/DispatchStatusBadge";
import { useUnreadWins } from "@/hooks/useUnreadWins";
import { bucketForRow, TAB_LABEL, TAB_ORDER, type BrokerTabKey } from "@/lib/lrDirect/brokerLoadBucket";
import { brokerRowStatus } from "@/lib/lrDirect/brokerRowStatus";
import { displayCompanyName } from "@/lib/displayCompanyName";
// BrokerLoadDispatchDrawer removed — "Open Dispatch" now navigates to the
// full-page broker dispatch view at /lr-direct/dispatches/won/:loadId.

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

interface BidRow {
  bid_id: string;
  bid_amount: number;
  bid_status: string;
  bid_submitted_at: string | null;

  load_id: string;
  load_number: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  pickup_date: string;
  total_miles: number | null;
  load_status: string;
  awarded_at: string | null;
  awarded_bid_id: string | null;
  created_by: string | null;

  dispatch_id: string | null;
  dispatch_status: string | null;
  dispatch_archived_at: string | null;
  dispatch_permanent_delete_at: string | null;

  is_awarded: boolean;
  lost_to_other: boolean;
  load_missing: boolean;
  bucket: Exclude<BrokerTabKey, "all">;
}

function formatPickup(dateStr: string): string {
  const d = parseYYYYMMDDToLocalDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatRel(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const hrs = Math.floor(diffMs / 3_600_000);
  if (hrs < 1) return "Just now";
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86_400_000);
}

function BrokerStatusBadge({ row }: { row: BidRow }) {
  const { label, tone } = brokerRowStatus(row);
  return <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${tone}`}>{label}</Badge>;
}

export default function LRDirectMyWinsPage() {
  usePageMeta({
    title: "LR Direct — My Loads | LaneRunner",
    description: "All your LR Direct bids and awarded loads in one workspace, with dispatch status updates that flow to the shipper.",
  });

  const { companyId } = useCompanyAccount();
  const { user } = useAuth();
  const navigate = useNavigate();

  const markWinRead = (loadId: string) => {
    if (!user?.id) return;
    supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("recipient_user_id", user.id)
      .eq("type", "bid_won")
      .eq("is_read", false)
      .filter("metadata->>direct_load_id", "eq", loadId)
      .then(() => { /* noop */ });
  };

  const unreadWinsCount = useUnreadWins(user?.id ?? null);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq("recipient_user_id", user.id)
      .eq("type", "bid_won")
      .eq("is_read", false)
      .then(() => { /* noop */ });
  }, [user?.id]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<BidRow[]>([]);
  const [shipperNames, setShipperNames] = useState<Record<string, string>>({});
  const [reloadTick, setReloadTick] = useState(0);
  const [tab, setTab] = useState<BrokerTabKey>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [confirmDelete, setConfirmDelete] = useState<BidRow | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [confirmWithdraw, setConfirmWithdraw] = useState<BidRow | null>(null);
  // drawerRow removed — Open Dispatch navigates instead of opening a drawer.

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setRows([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: bids, error: bidsErr } = await supabase
          .from("direct_bids")
          .select("id, bid_amount, status, submitted_at, direct_load_id")
          .eq("broker_company_id", companyId)
          .order("submitted_at", { ascending: false });

        if (bidsErr) throw bidsErr;
        if (cancelled) return;

        const bidList = bids ?? [];
        if (bidList.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        const loadIds = Array.from(new Set(bidList.map((b: any) => b.direct_load_id)));
        const { data: loads, error: loadsErr } = await supabase
          .from("direct_loads")
          .select(`
            id, load_number, status, created_by,
            origin_city, origin_state,
            destination_city, destination_state,
            equipment_type, pickup_date, total_miles,
            awarded_at, awarded_bid_id
          `)
          .in("id", loadIds);

        if (loadsErr) throw loadsErr;
        if (cancelled) return;

        const loadMap = new Map<string, any>((loads ?? []).map((l: any) => [l.id, l]));

        const dispatchMap = new Map<string, { id: string; status: string; archived_at: string | null; permanent_delete_at: string | null }>();
        const { data: dispatches } = await supabase
          .from("direct_dispatches" as any)
          .select("id, direct_load_id, status, archived_at, permanent_delete_at")
          .in("direct_load_id", loadIds);
        (dispatches as any[] | null ?? []).forEach((d) =>
          dispatchMap.set(d.direct_load_id, {
            id: d.id, status: d.status,
            archived_at: d.archived_at ?? null,
            permanent_delete_at: d.permanent_delete_at ?? null,
          })
        );

        const out: BidRow[] = bidList.map((b: any) => {
          const l = loadMap.get(b.direct_load_id);
          const d = l ? dispatchMap.get(l.id) : null;
          const isAwarded = !!l && l.awarded_bid_id === b.id;
          const lostToOther = !!l && !!l.awarded_bid_id && l.awarded_bid_id !== b.id;

          const displayBidStatus = lostToOther && b.status !== "withdrawn" ? "lost" : b.status;

          const bucket = bucketForRow({
            bid_status: b.status,
            is_awarded: isAwarded,
            lost_to_other: lostToOther,
            dispatch_status: d?.status ?? null,
            dispatch_archived_at: d?.archived_at ?? null,
            load_status: l?.status ?? null,
            load_missing: !l,
          });

          return {
            bid_id: b.id,
            bid_amount: Number(b.bid_amount),
            bid_status: displayBidStatus,
            bid_submitted_at: b.submitted_at ?? null,
            load_id: l?.id ?? b.direct_load_id,
            load_number: l?.load_number ?? "—",
            origin_city: l?.origin_city ?? "",
            origin_state: l?.origin_state ?? "",
            destination_city: l?.destination_city ?? "",
            destination_state: l?.destination_state ?? "",
            equipment_type: l?.equipment_type ?? "",
            pickup_date: l?.pickup_date ?? "",
            total_miles: l?.total_miles ?? null,
            load_status: l?.status ?? "",
            awarded_at: l?.awarded_at ?? null,
            awarded_bid_id: l?.awarded_bid_id ?? null,
            created_by: l?.created_by ?? null,
            dispatch_id: d?.id ?? null,
            dispatch_status: d?.status ?? null,
            dispatch_archived_at: d?.archived_at ?? null,
            dispatch_permanent_delete_at: d?.permanent_delete_at ?? null,
            is_awarded: isAwarded,
            lost_to_other: lostToOther,
            load_missing: !l,
            bucket,
          };
        });

        setRows(out);

        const shipperIds = Array.from(new Set(out.map((r) => r.created_by).filter(Boolean) as string[]));
        if (shipperIds.length > 0) {
          const { data: profs } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, email, company_name")
            .in("id", shipperIds);
          const map: Record<string, string> = {};
          (profs ?? []).forEach((p: any) => {
            const full = [p.first_name, p.last_name].filter(Boolean).join(" ");
            map[p.id] = displayCompanyName(p.company_name) || full || p.email || "Shipper";
          });
          if (!cancelled) setShipperNames(map);
        }

        setLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "Failed to load bids.");
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, reloadTick]);

  const counts = useMemo(() => {
    const c: Record<BrokerTabKey, number> = {
      all: rows.length, bidding: 0, awarded: 0, dispatching: 0,
      in_transit: 0, delivered: 0, closed: 0, archive: 0,
    };
    rows.forEach((r) => { c[r.bucket]++; });
    return c;
  }, [rows]);

  const filtered = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.bucket === tab)),
    [rows, tab],
  );

  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Reset to page 1 whenever the tab or page size changes.
  useEffect(() => { setPage(1); }, [tab, pageSize]);
  // Clamp page if data shrinks (e.g. after archive/delete).
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const withdrawBid = async (r: BidRow) => {
    const { data, error } = await supabase.rpc("withdraw_direct_bid" as any, { p_bid_id: r.bid_id, p_reason: null });
    if (error) { toast.error(error.message); return; }
    const res = data as any;
    if (res && res.success === false) { toast.error(res.error ?? "Could not withdraw bid"); return; }
    toast.success("Bid withdrawn");
    setConfirmWithdraw(null);
    setReloadTick((t) => t + 1);
  };

  const archiveDispatch = async (r: BidRow) => {
    if (!r.dispatch_id) { toast.error("No dispatch record yet."); return; }
    const { error } = await supabase.rpc("archive_direct_dispatch" as any, { p_dispatch_id: r.dispatch_id });
    if (error) { toast.error(error.message); return; }
    toast.success("Moved to Archive (auto-deletes in 14 days)");
    setReloadTick((t) => t + 1);
  };

  const unarchiveDispatch = async (r: BidRow) => {
    if (!r.dispatch_id) return;
    const { error } = await supabase.rpc("unarchive_direct_dispatch" as any, { p_dispatch_id: r.dispatch_id });
    if (error) { toast.error(error.message); return; }
    toast.success("Restored");
    setReloadTick((t) => t + 1);
  };

  const deletePermanently = async () => {
    if (!confirmDelete?.dispatch_id) {
      setConfirmDelete(null); setConfirmText("");
      return;
    }
    const { error } = await supabase.rpc("delete_direct_dispatch_permanently" as any, { p_dispatch_id: confirmDelete.dispatch_id });
    if (error) { toast.error(error.message); return; }
    toast.success("Dispatch deleted permanently");
    setConfirmDelete(null);
    setConfirmText("");
    setReloadTick((t) => t + 1);
  };

  const openDispatch = (r: BidRow) => {
    markWinRead(r.load_id);
    navigate(`/lr-direct/dispatches/won/${r.load_id}`);
  };

  const rowActions = (r: BidRow) => {
    const canOpenDispatch = r.is_awarded && r.bucket !== "archive";
    const canEditBid = r.bucket === "bidding";
    const canWithdraw = r.bucket === "bidding" && r.bid_status === "pending";
    const isArchive = r.bucket === "archive";

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {canOpenDispatch && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px] gap-1"
            onClick={() => openDispatch(r)}
          >
            <Truck className="h-3 w-3" /> Open Dispatch
          </Button>
        )}
        {canEditBid && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px] gap-1"
            onClick={() => navigate(`/lr-direct/opportunity/${r.load_id}`, { state: { from: "my-wins" } })}
          >
            <Pencil className="h-3 w-3" /> Edit Bid
          </Button>
        )}
        {canWithdraw && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px] gap-1 text-destructive hover:text-destructive"
            onClick={() => setConfirmWithdraw(r)}
          >
            <XCircle className="h-3 w-3" /> Withdraw
          </Button>
        )}
        {isArchive && !r.load_missing && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-[11px] gap-1"
            onClick={() => navigate(`/lr-direct/opportunity/${r.load_id}`, { state: { from: "my-wins" } })}
          >
            <Eye className="h-3 w-3" /> View
          </Button>
        )}
        {r.created_by && !isArchive && (
          <MessageShipperButton
            loadId={r.load_id}
            shipperId={r.created_by}
            shipperName={shipperNames[r.created_by] ?? "Shipper"}
          />
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-md bg-brand-teal/10 flex items-center justify-center">
          <Package className="h-5 w-5 text-brand-teal" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">My Loads</h1>
          <p className="text-sm text-muted-foreground">
            Every bid and awarded load your company touches on LR Direct — including dispatch updates that flow to the shipper.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as BrokerTabKey)} className="mt-4 mb-3">
        <TabsList className="flex flex-wrap h-auto">
          {TAB_ORDER.map((k) => (
            <TabsTrigger key={k} value={k} className="relative">
              {TAB_LABEL[k]} ({counts[k]})
              {k === "awarded" && unreadWinsCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[9px] font-bold align-middle">
                  {unreadWinsCount > 9 ? "9+" : unreadWinsCount}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-md border border-border bg-card">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-destructive mb-3">{error}</p>
            <Button size="sm" variant="outline" onClick={() => setReloadTick((t) => t + 1)}>Retry</Button>
          </div>
        ) : !companyId ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Your account isn't linked to a company yet.
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium text-foreground">Nothing here yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              {tab === "bidding" && "Place a bid on the LR Direct Board and it will show up here."}
              {tab === "awarded" && "Awarded loads that haven't been dispatched yet will appear here."}
              {tab === "archive" && "Withdrawn, lost, and archived bids land here."}
              {(tab === "all" || tab === "dispatching" || tab === "in_transit" || tab === "delivered" || tab === "closed") &&
                "Nothing in this stage right now."}
            </p>
          </div>
        ) : (
          <>
          <div className="flex items-center justify-between gap-2 px-3 pt-3">
            <p className="text-xs text-muted-foreground font-medium">
              Showing <span className="text-foreground font-semibold">{totalRows === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalRows)}</span> of <span className="text-foreground font-semibold">{totalRows}</span> load{totalRows !== 1 ? "s" : ""}
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <Pager page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden flex flex-col gap-2 p-3">
            {pagedRows.map((r) => (
              <button
                key={`m-${r.bid_id}`}
                type="button"
                onClick={() => r.is_awarded ? openDispatch(r) : navigate(`/lr-direct/opportunity/${r.load_id}`, { state: { from: "my-wins" } })}
                className="text-left rounded-lg border border-border bg-card p-3 active:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-teal min-w-0"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono text-xs font-medium truncate min-w-0">{r.load_number}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <BrokerStatusBadge row={r} />
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                <div className="text-sm font-semibold truncate">
                  {r.origin_city}, {r.origin_state} → {r.destination_city}, {r.destination_state}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {formatEquipmentType(r.equipment_type)} · {formatPickup(r.pickup_date)}
                  {r.total_miles ? ` · ${Math.round(Number(r.total_miles)).toLocaleString()} mi` : ""}
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    {r.created_by ? (shipperNames[r.created_by] ?? "Shipper") : "—"} · {formatRel(r.bid_submitted_at)}
                  </span>
                  <span className="text-sm font-semibold tabular-nums">${Math.round(r.bid_amount).toLocaleString()}</span>
                </div>
                {r.bucket === "archive" && r.dispatch_archived_at && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Auto-deletes in {daysUntil(r.dispatch_permanent_delete_at) ?? "—"} days
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Load #</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Lane</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Equipment</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Pickup</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide text-right">Miles</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Shipper</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide text-right">Your Bid</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Status</TableHead>
                <TableHead className="h-10 text-xs font-semibold uppercase tracking-wide">Actions</TableHead>
                <TableHead className="h-10 w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.map((r) => (
                <TableRow
                  key={r.bid_id}
                  className="cursor-pointer"
                  onClick={() => r.is_awarded ? openDispatch(r) : navigate(`/lr-direct/opportunity/${r.load_id}`, { state: { from: "my-wins" } })}
                >
                  <TableCell className="py-2.5 font-mono text-xs font-medium">
                    {r.load_number}
                    {r.bucket === "archive" && r.dispatch_archived_at && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Auto-deletes in {daysUntil(r.dispatch_permanent_delete_at) ?? "—"}d
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-sm">
                    <span className="font-medium">{r.origin_city}, {r.origin_state}</span>
                    <span className="text-muted-foreground mx-1.5">→</span>
                    <span className="font-medium">{r.destination_city}, {r.destination_state}</span>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm">{formatEquipmentType(r.equipment_type)}</TableCell>
                  <TableCell className="py-2.5 text-sm">{formatPickup(r.pickup_date)}</TableCell>
                  <TableCell className="py-2.5 text-sm text-right tabular-nums">
                    {r.total_miles ? Math.round(Number(r.total_miles)).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-sm truncate max-w-[160px]">
                    {r.created_by ? (shipperNames[r.created_by] ?? "Shipper") : "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-right tabular-nums font-medium">
                    ${Math.round(r.bid_amount).toLocaleString()}
                  </TableCell>
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-1.5">
                      <BrokerStatusBadge row={r} />
                      <span className="text-[11px] text-muted-foreground">{formatRel(r.bid_submitted_at)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5" onClick={(e) => e.stopPropagation()}>
                    {rowActions(r)}
                  </TableCell>
                  <TableCell className="py-2.5" onClick={(e) => e.stopPropagation()}>
                    {r.is_awarded && r.bucket !== "archive" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => archiveDispatch(r)}>
                            <Archive className="h-4 w-4 mr-2" /> Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {r.bucket === "archive" && r.dispatch_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => unarchiveDispatch(r)}>
                            <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => { setConfirmDelete(r); setConfirmText(""); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          <div className="flex items-center justify-between gap-2 px-3 pb-3">
            <p className="text-xs text-muted-foreground font-medium">
              Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{totalPages}</span>
            </p>
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </div>
          </>
        )}
      </div>

      <AlertDialog open={!!confirmWithdraw} onOpenChange={(o) => { if (!o) setConfirmWithdraw(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this bid?</AlertDialogTitle>
            <AlertDialogDescription>
              Your bid of <strong>${confirmWithdraw ? Math.round(confirmWithdraw.bid_amount).toLocaleString() : ""}</strong> on{" "}
              <strong>{confirmWithdraw?.load_number}</strong> will be removed from the shipper's offer list and moved to Archive. You can place a new bid before the deadline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmWithdraw && withdrawBid(confirmWithdraw)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Withdraw bid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setConfirmText(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this dispatch?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the dispatch packet and its history for{" "}
              <strong>{confirmDelete?.load_number}</strong>. The underlying load record is kept.
              This cannot be undone. Type <strong>DELETE</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "DELETE"}
              onClick={deletePermanently}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  // Show up to 5 numbered pages centered around the current page.
  const window = 5;
  let start = Math.max(1, page - Math.floor(window / 2));
  const end = Math.min(totalPages, start + window - 1);
  start = Math.max(1, end - window + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
        aria-label="Previous page"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      {start > 1 && (
        <>
          <button onClick={() => onChange(1)} className="inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium text-muted-foreground hover:bg-muted">1</button>
          {start > 2 && <span className="text-muted-foreground text-[11px] px-0.5">…</span>}
        </>
      )}
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={cn(
            "inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium transition-colors",
            p === page ? "bg-brand-teal text-white" : "text-muted-foreground hover:bg-muted"
          )}
        >
          {p}
        </button>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-muted-foreground text-[11px] px-0.5">…</span>}
          <button onClick={() => onChange(totalPages)} className="inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium text-muted-foreground hover:bg-muted">{totalPages}</button>
        </>
      )}
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
        aria-label="Next page"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function MessageShipperButton({ loadId, shipperId, shipperName }: { loadId: string; shipperId: string; shipperName: string }) {
  const { openChatWithUser } = useChatSystem();
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 px-2 text-[11px] gap-1"
      onClick={() => openChatWithUser(shipperId, shipperName, undefined, loadId)}
      title={`Message ${shipperName}`}
    >
      <MessageSquare className="h-3 w-3" />
    </Button>
  );
}
